import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  computed,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ProgressBarComponent } from '../../progress-bar/progress-bar.component';

export interface FileDropzoneConfig {
  acceptedTypes?: string[];
  maxFileSize?: number; // in bytes
  multiple?: boolean;
  disabled?: boolean;
}

export interface FileUploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

let nextId = 0;

/**
 * A file drop area: one button that opens the file picker and also takes a
 * dropped file. It shows the chosen file(s), why a file was refused, upload
 * progress and an error on one reserved status line, all in a box of fixed
 * height. The dialog around it owns the primary action.
 */
@Component({
  selector: 'app-file-dropzone',
  templateUrl: './file-dropzone.component.html',
  styleUrls: ['./file-dropzone.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ProgressBarComponent],
})
export class FileDropzoneComponent {
  config = input<FileDropzoneConfig>({
    acceptedTypes: ['.step', '.stp'],
    maxFileSize: 50 * 1024 * 1024, // 50MB default
    multiple: false,
    disabled: false,
  });

  title = input<string>('Choose a file');
  subtitle = input<string>('Supported formats');
  changeLabel = input<string>('Choose another file');
  description = input<string>('or drop it here');
  /** Shown on the status line while uploading (e.g. "Processing the model…"). */
  customUploadMessage = input<string | null>(null);
  isUploading = input<boolean>(false);
  uploadProgress = input<FileUploadProgress | null>(null);
  errorMessage = input<string | null>(null);
  /** The chosen files; the area shows these, and follows the owner when it changes them. */
  selectedFiles = input<File[]>([]);
  /** Accessible name of the drop area when the title alone doesn't say what the file is for. */
  ariaLabel = input<string | null>(null);

  filesSelected = output<File[]>();
  filesDropped = output<File[]>();
  uploadCanceled = output<void>();

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  private readonly id = nextId++;
  readonly statusId = `if-dropzone-${this.id}-status`;
  readonly hintId = `if-dropzone-${this.id}-hint`;

  readonly isDragOver = signal(false);
  /** Why the last picked or dropped file(s) were refused. */
  readonly rejection = signal<string | null>(null);

  /** Follows `selectedFiles`; a pick or drop sets it directly. */
  readonly files = linkedSignal<File[], File[]>({
    source: this.selectedFiles,
    computation: (src) => src ?? [],
  });

  readonly isDisabled = computed(() => !!this.config().disabled || this.isUploading());
  readonly hasFiles = computed(() => this.files().length > 0);
  readonly multiple = computed(() => !!this.config().multiple);
  readonly progressPercentage = computed(() => this.uploadProgress()?.percentage ?? 0);

  readonly acceptedTypesString = computed(() => this.config().acceptedTypes?.join(', ') || '');

  /** The quiet line under the title: formats and size limit, or the file size and how to change it. */
  readonly metaLine = computed(() => {
    const files = this.files();
    if (this.isUploading()) return `${Math.round(this.progressPercentage())}% uploaded`;
    if (files.length === 1) return `${this.formatFileSize(files[0].size)} · ${this.changeLabel()}`;
    if (files.length > 1) return `${this.formatFileSize(files.reduce((n, f) => n + f.size, 0))} in total · ${this.changeLabel()}`;
    const parts = [this.description()];
    const max = this.config().maxFileSize;
    if (max) parts.push(`up to ${this.formatFileSize(max)}`);
    return parts.filter(Boolean).join(' · ');
  });

  readonly titleLine = computed(() => {
    const files = this.files();
    if (this.isUploading()) return files.length === 1 ? `Uploading ${files[0].name}` : 'Uploading';
    if (files.length === 1) return files[0].name;
    if (files.length > 1) return `${files.length} files chosen`;
    return this.title();
  });

  /** Formats line, shown while nothing is chosen. */
  readonly formatsLine = computed(() => {
    const types = this.acceptedTypesString();
    if (!types) return '';
    return this.subtitle() ? `${this.subtitle()}: ${types}` : '';
  });

  /** One reserved line: refusal or error first, then the upload message. */
  readonly status = computed<{ text: string; tone: 'error' | 'info' } | null>(() => {
    const error = this.rejection() ?? this.errorMessage();
    if (error) return { text: error, tone: 'error' };
    if (this.isUploading() && this.customUploadMessage()) return { text: this.customUploadMessage()!, tone: 'info' };
    return null;
  });

  readonly areaLabel = computed(() => {
    const name = this.ariaLabel();
    const title = this.titleLine();
    return name ? `${name}: ${title}` : null;
  });

  openPicker(): void {
    if (this.isDisabled()) return;
    const el = this.fileInput()?.nativeElement;
    if (!el) return;
    el.value = '';
    el.click();
  }

  onDragOver(event: DragEvent): void {
    if (this.isDisabled()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    if (this.isDisabled()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    if (this.isDisabled()) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
    const files = Array.from(event.dataTransfer?.files || []);
    this.handleFiles(files, true);
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.handleFiles(Array.from(input.files || []), false);
  }

  removeFile(index: number): void {
    if (this.isDisabled()) return;
    const next = this.files().filter((_, i) => i !== index);
    this.files.set(next);
    this.rejection.set(null);
    this.filesSelected.emit(next);
  }

  clearFiles(): void {
    if (this.isDisabled()) return;
    this.files.set([]);
    this.rejection.set(null);
    this.filesSelected.emit([]);
  }

  cancelUpload(): void {
    this.uploadCanceled.emit();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private handleFiles(files: File[], dropped: boolean): void {
    if (files.length === 0) return;
    const { valid, refused } = this.validateFiles(files);
    this.rejection.set(refused);
    if (valid.length === 0) return;
    const chosen = this.multiple() ? valid : [valid[0]];
    this.files.set(chosen);
    this.filesSelected.emit(chosen);
    if (dropped) this.filesDropped.emit(chosen);
  }

  /** Splits files into usable ones and a sentence saying why the others were refused. */
  private validateFiles(files: File[]): { valid: File[]; refused: string | null } {
    const cfg = this.config();
    const valid: File[] = [];
    const reasons: string[] = [];

    for (const file of files) {
      if (cfg.acceptedTypes && cfg.acceptedTypes.length > 0) {
        const extension = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
        if (!cfg.acceptedTypes.includes(extension)) {
          reasons.push(`${file.name} isn't a ${cfg.acceptedTypes.join(' or ')} file.`);
          continue;
        }
      }
      if (cfg.maxFileSize && file.size > cfg.maxFileSize) {
        reasons.push(`${file.name} is larger than ${this.formatFileSize(cfg.maxFileSize)}.`);
        continue;
      }
      valid.push(file);
    }

    if (!this.multiple() && files.length > 1 && valid.length > 1) {
      reasons.push('Only one file can be used; the first one was taken.');
    }
    const refused = reasons.length === 0 ? null : reasons.length === 1 ? reasons[0] : `${reasons[0]} (${reasons.length - 1} more not used)`;
    return { valid, refused };
  }
}
